import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import experiences from "../data/experiences.json";

// Register Helvetica (built-in, closest to Arial for ATS)
Font.register({
  family: "Helvetica",
  fonts: [
    { src: "Helvetica" },
    { src: "Helvetica-Bold", fontWeight: "bold" },
    { src: "Helvetica-Oblique", fontStyle: "italic" },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    padding: 72, // 1 inch margins (72pt = 1in)
    color: "#000",
    lineHeight: 1.4,
  },
  // Contact header
  name: {
    fontSize: 18,
    fontWeight: "bold",
    lineHeight: 1.0,
    marginBottom: 0,
  },
  headline: {
    fontSize: 10,
    lineHeight: 1.0,
    marginTop: 0,
    marginBottom: 3,
    color: "#333",
  },
  contactRow: {
    fontSize: 9,
    color: "#333",
    marginBottom: 2,
  },
  // Section heading
  sectionHeading: {
    fontSize: 11,
    fontWeight: "bold",
    textTransform: "uppercase",
    borderBottomWidth: 1.5,
    borderBottomColor: "#000",
    paddingBottom: 2,
    marginTop: 10,
    marginBottom: 4,
  },
  // Summary
  summary: {
    fontSize: 10,
    lineHeight: 1.5,
    marginBottom: 2,
  },
  // Experience
  jobHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 6,
    marginBottom: 1,
  },
  jobTitle: {
    fontSize: 10,
    fontWeight: "bold",
  },
  jobDate: {
    fontSize: 9,
    fontStyle: "italic",
    color: "#333",
  },
  jobLocation: {
    fontSize: 9,
    fontStyle: "italic",
    color: "#555",
    marginBottom: 2,
  },
  bullet: {
    flexDirection: "row",
    marginBottom: 1,
    paddingLeft: 8,
  },
  bulletDot: {
    width: 10,
    fontSize: 10,
  },
  bulletText: {
    flex: 1,
    fontSize: 9,
    lineHeight: 1.4,
  },
  // Skills
  skillsText: {
    fontSize: 9,
    lineHeight: 1.6,
  },
  // Education
  eduEntry: {
    marginBottom: 3,
  },
  eduDegree: {
    fontSize: 10,
    fontWeight: "bold",
  },
  eduSchool: {
    fontSize: 9,
  },
  eduPeriod: {
    fontSize: 9,
    fontStyle: "italic",
    color: "#555",
  },
  // Certifications
  certEntry: {
    fontSize: 9,
    marginBottom: 2,
  },
  // Languages
  langText: {
    fontSize: 9,
  },
});

function splitPeriod(period = "") {
  const [range, loc] = (period || "").split("·").map((s) => s.trim());
  return { dateText: range || period || "", location: loc || "" };
}

export default function ResumePDF({ data }) {
  return (
    <Document title="Steven_Li_Resume" author="Steven Li">
      <Page size="LETTER" style={styles.page}>
        {/* CONTACT */}
        <Text style={styles.name}>{data.name}</Text>
        <Text style={styles.headline}>{data.headline}</Text>
        <Text style={styles.contactRow}>
          {data.location} | {data.email} | {data.phone} | {data.linkedin.replace("https://www.", "")}
        </Text>

        {/* SUMMARY */}
        <Text style={styles.sectionHeading}>Summary</Text>
        <Text style={styles.summary}>{data.summary}</Text>

        {/* CORE COMPETENCIES */}
        <Text style={styles.sectionHeading}>Core Competencies</Text>
        <Text style={styles.skillsText}>
          {data.coreCompetencies.join(" | ")}
        </Text>

        {/* PROFESSIONAL EXPERIENCE */}
        <Text style={styles.sectionHeading}>Professional Experience</Text>
        {experiences.map((job, i) => {
          const { dateText, location } = splitPeriod(job.period);
          return (
            <View key={i} wrap={false}>
              <View style={styles.jobHeader}>
                <Text style={styles.jobTitle}>
                  {job.title} — {job.company}
                </Text>
                <Text style={styles.jobDate}>{dateText}</Text>
              </View>
              {location ? (
                <Text style={styles.jobLocation}>{location}</Text>
              ) : null}
              {job.bullets.map((b, bi) => (
                <View style={styles.bullet} key={bi}>
                  <Text style={styles.bulletDot}>•</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          );
        })}

        {/* EDUCATION */}
        <Text style={styles.sectionHeading}>Education</Text>
        {data.education.map((e, i) => (
          <View key={i} style={styles.eduEntry}>
            <Text style={styles.eduDegree}>{e.degree}</Text>
            <Text style={styles.eduSchool}>
              {e.school} ({e.period})
            </Text>
          </View>
        ))}

        {/* CERTIFICATIONS */}
        <Text style={styles.sectionHeading}>Certifications</Text>
        {data.certifications.map((c, i) => (
          <Text key={i} style={styles.certEntry}>
            {c.name}
            {c.org ? ` — ${c.org.name || c.org}` : ""}
            {c.year ? ` (${c.year})` : ""}
          </Text>
        ))}

        {/* LANGUAGES */}
        <Text style={styles.sectionHeading}>Languages</Text>
        <Text style={styles.langText}>{data.languages.join(" | ")}</Text>
      </Page>
    </Document>
  );
}
